"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { ProductVariant } from "@/hooks/useFunnelState";
import ProductVariantTable from "./price-customization/ProductVariantTable";
import { OfferResponse } from "@/types/simpego-api";
import { BaseCoverageId, INDEPENDENT_BASE_COVERAGE_IDS } from "@/config/coverages";
import CoverageSelection from "./pet-details/CoverageSelection";
import { createProductVariant } from "@/utils/funnel";

interface ProductVariantSelectionProps {
  petName: string;
  bundleRecommendations: { S: OfferResponse; M: OfferResponse; L: OfferResponse } | null;
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  onCoverageSelectionsChange: (next: Partial<Record<BaseCoverageId, boolean>>) => void;
  petBirthDate?: Date | null;
  petType?: "cat" | "dog" | null;
  onSelectAndNavigate: (product: ProductVariant) => void;
  onSelectOnly: (product: ProductVariant) => void;
  onSelectAndSkipToContact: (product: ProductVariant) => void;
  onBack: () => void;
  selectedProduct: ProductVariant | null;
  isLoadingPrice: boolean;
  paymentInterval: 1 | 2 | 4 | 12;
  isFetchingRecommendations: boolean;
  /** Set while Vorerkrankung is on; moving on to a plan then requires the categories. */
}

// Which tier is "our recommendation" is a placeholder pending sign-off with the product
// team (see plan's open risks) — used only as the fallback when the customer clicks
// "Weiter" without explicitly picking a package.
const PLACEHOLDER_RECOMMENDED_BUNDLE: "s" | "m" | "l" = "l";

const ProductVariantSelection: React.FC<ProductVariantSelectionProps> = ({
  petName,
  bundleRecommendations,
  coverageSelections,
  onCoverageSelectionsChange,
  petBirthDate,
  petType,
  onSelectAndNavigate,
  onSelectOnly,
  onSelectAndSkipToContact,
  onBack,
  selectedProduct,
  isLoadingPrice,
  paymentInterval,
  isFetchingRecommendations,
}) => {
  const { t } = useTranslation();
  const [coverageError, setCoverageError] = React.useState("");
  const coverageGroupRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const productVariants: ProductVariant[] = React.useMemo(
    () => (["s", "m", "l"] as const).map((id) => createProductVariant(id)),
    [t]
  );

  // Same rule as step 1: at least one Grunddeckung must stay selected.
  const handleCoverageChange = (next: Partial<Record<BaseCoverageId, boolean>>) => {
    if (!INDEPENDENT_BASE_COVERAGE_IDS.some((id) => next[id])) {
      setCoverageError(t("coverages.validation.cannotDeselectLast"));
      return;
    }
    setCoverageError("");
    onCoverageSelectionsChange(next);
  };

  const handleContinue = () => {
    const product = selectedProduct ?? productVariants.find((v) => v.id === PLACEHOLDER_RECOMMENDED_BUNDLE)!;
    onSelectAndSkipToContact(product);
  };

  return (
    <Card className="w-full max-w-6xl mx-auto mb-24 border-none shadow-none">
      <CardHeader className="px-0">
        <CardTitle className="text-3xl font-bold">
          {t("productVariantSelection.title")}
        </CardTitle>
        <CardDescription>
          {t("productVariantSelection.description", { petName: petName?.trim() || t("common.yourPet") })}
        </CardDescription>
      </CardHeader>
      <CardContent className="px-0 space-y-6 md:space-y-0 pt-2 md:pt-0">
        <CoverageSelection
          coverageSelections={coverageSelections}
          setCoverageSelections={handleCoverageChange}
          error={coverageError}
          isLoading={isLoadingPrice}
          groupRef={coverageGroupRef}
          hideTitle
        />
        <ProductVariantTable
          productVariants={productVariants}
          selectedProduct={selectedProduct}
          bundleRecommendations={bundleRecommendations}
          coverageSelections={coverageSelections}
          petBirthDate={petBirthDate}
          petType={petType}
          recommendedId={PLACEHOLDER_RECOMMENDED_BUNDLE}
          onSelectOnly={onSelectOnly}
          onSelectAndSkipToContact={onSelectAndSkipToContact}
          onSelectAndCustomize={onSelectAndNavigate}
          isLoadingPrice={isLoadingPrice}
          paymentInterval={paymentInterval}
          isFetchingRecommendations={isFetchingRecommendations}
        />
      </CardContent>
      <CardFooter className="flex flex-col gap-2 px-0">
        <div className="flex w-full flex-col-reverse gap-3 md:flex-row md:gap-4">
          <Button variant="outline" onClick={onBack} className="w-full h-12 text-lg font-bold" disabled={isLoadingPrice}>
            {t("productVariantSelection.back")}
          </Button>
          <Button onClick={handleContinue} className="w-full h-12 text-lg font-bold bg-continue-button text-continue-button-foreground hover:bg-continue-button/90" disabled={isLoadingPrice}>
            {isLoadingPrice && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("productVariantSelection.next")}
          </Button>
        </div>
        {!selectedProduct && (
          <p className="text-center text-sm text-muted-foreground">
            {t("productVariantSelection.autoRecommendationHint")}
          </p>
        )}
      </CardFooter>
    </Card>
  );
};

export default ProductVariantSelection;
